/**
 * Cross-app export helpers.
 *
 * Provides utilities to convert data from any app into App 1 (Flashcard)
 * format, and to download/import JSON files.
 */

import { generateUUID } from './uuid';
import { today } from './dateUtils';

/**
 * Create a new flashcard object from arbitrary source data.
 * Used by Exam Simulator and Protocol Matrix to export to flashcards.
 */
export function createFlashcard({ front, back, domain, subdomain = '', tags = [], notes = '', source = 'user' }) {
  return {
    id: generateUUID(),
    domain,
    subdomain,
    front,
    back,
    tags,
    notes,
    source,
    created_at: new Date().toISOString(),
    sr_data: {
      easiness_factor: 2.5,
      interval_days: 0,
      repetitions: 0,
      next_review_date: today(),
      last_review_date: null,
    },
    stats: {
      times_seen: 0,
      times_correct: 0,
      streak: 0,
    },
  };
}

/**
 * Convert an exam question (that was answered wrong) into a flashcard.
 */
export function examQuestionToFlashcard(question, userAnswer) {
  const correctText = Array.isArray(question.correct_answer)
    ? question.correct_answer.join(', ')
    : question.correct_answer;

  return createFlashcard({
    front: question.prompt,
    back: `**Correct Answer:** ${correctText}\n\n${question.explanation}`,
    domain: question.domain,
    subdomain: question.subdomain || '',
    tags: [...(question.tags || []), 'exam-review'],
    notes: question.expert_trap_warning || '',
    source: 'exam-export',
  });
}

/**
 * Convert a protocol matrix cell into a flashcard.
 */
export function matrixCellToFlashcard(matrixTitle, protocol, attributeName, value, domain) {
  return createFlashcard({
    front: `What is **${protocol}**'s **${attributeName}**?`,
    back: value,
    domain,
    subdomain: matrixTitle,
    tags: [protocol.toLowerCase(), 'matrix-export'],
    source: 'matrix-export',
  });
}

/**
 * Trigger a JSON file download in the browser.
 */
export function downloadAsJSON(data, filename) {
  const json = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Read a JSON file from a File input. Returns a Promise.
 */
export function readJSONFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        resolve(JSON.parse(e.target.result));
      } catch (err) {
        reject(new Error('Invalid JSON file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}
